// Common chord progressions in Roman numeral notation
// Each progression is represented as an array of scale degrees (1-based)
// For example, [1, 4, 5] represents I-IV-V progression
//
// NOTE S7 Phase A : la donnee des progressions vit dans la base (source unique). Le
// runtime lit CHORD_PROGRESSIONS depuis le miroir (src/data/contentStore). L'overlay des
// progressions user (App.js) se superpose sur cet objet vif ; le miroir le merge sans l'ecraser.
import { CHORD_PROGRESSIONS } from '../data/contentStore';
import { getUserMap, saveUserItem } from '../data/userStore';


// Get user progressions from the user mirror (scope 'progression')
const getUserProgressions = () => {
  try {
    return getUserMap('progression');
  } catch {
    return {};
  }
};

// Save a user progression into the mirror (write-through IndexedDB)
const saveUserProgression = (name, prog) => saveUserItem('progression', name, prog);

// Modal progression mappings for different scale types
const MODAL_PROGRESSIONS = {
  // Melodic Minor modes
  'Dorian b2': 'Dorian Modal (i-IV-i-VII-i)',
  'Lydian Augmented': 'Lydian Modal (I-II-I-II)',
  'Lydian Dominant': 'Jazz Turnaround (ii-V-I)',
  'Mixolydian b6': 'Pop Classic (I-IV-V-I)',
  'Locrian #2': 'Jazz Minor (i-iiiø-vi-ii)',
  'Altered': 'Jazz Turnaround (ii-V-I)',
  
  // Harmonic Minor modes
  'Harmonic Minor': 'Harmonic Minor (i-iv-V-i)',
  'Locrian #6': 'Locrian Modal (i°-V-i°-V)',
  'Ionian #5': 'Pop Modern (I-V-vi-IV)',
  'Dorian #4': 'Dorian Modal (i-IV-i-VII-i)',
  'Phrygian Dominant': 'Phrygian Dominant (I-ii-I-ii)',
  'Lydian #2': 'Lydian Modal (I-II-I-II)',
  'Ultralocrian': 'Diminished (i°-iii°-v°-vii°)',
  
  // Harmonic Major modes
  'Harmonic Major': 'Pop Classic (I-IV-V-I)',
  'Dorian b5': 'Dorian Modal (i-IV-i-VII-i)',
  'Phrygian b4': 'Phrygian Modal (i-ii-i-ii)',
  'Lydian b3': 'Jazz Minor (i-iiiø-vi-ii)',
  'Mixolydian b2': 'Phrygian Modal (i-ii-i-ii)',
  'Lydian Augmented #2': 'Lydian Modal (I-II-I-II)',
  'Locrian bb7': 'Locrian Modal (i°-V-i°-V)',
  
  // Double Harmonic modes
  'Double Harmonic': 'Double Harmonic (I-ii-I-ii)',
  'Lydian #2 #6': 'Lydian Modal (I-II-I-II)',
  'Ultraphrygian': 'Phrygian Modal (i-ii-i-ii)',
  'Hungarian Minor': 'Harmonic Minor (i-iv-V-i)',
  'Oriental': 'Phrygian Dominant (I-ii-I-ii)',
  'Ionian #2 #5': 'Pop Modern (I-V-vi-IV)',
  'Locrian bb3 bb7': 'Diminished (i°-iii°-v°-vii°)',
  
  // Exotic scales
  'Enigmatic': 'Whole Tone (I+-ii+-I+)',
  'Blues': 'Blues Progression (I7-IV7-V7-I7)',
  'Major Pentatonic': 'Pentatonic Modal (I-vi-IV-V)',
  'Minor Pentatonic': 'Pentatonic Modal (I-vi-IV-V)',
  'Whole Tone': 'Whole Tone (I+-ii+-I+)',
  'Half-Whole Diminished': 'Diminished (i°-iii°-v°-vii°)',
  'Whole-Half Diminished': 'Diminished (i°-iii°-v°-vii°)',
  'Augmented': 'Whole Tone (I+-ii+-I+)',
  'Prometheus': 'Whole Tone (I+-ii+-I+)'
};

// Get a list of all progression names for dropdown menus
const getProgressionNames = () => {
  const userProgressions = getUserProgressions();
  const combined = [...Object.keys(CHORD_PROGRESSIONS), ...Object.keys(userProgressions)];
  return [...new Set(combined)];
};

// Get a progression by name
const getProgressionByName = (name) => {
  // Special case for "User Progression" - return the current unsaved progression
  if (name === 'User Progression') {
    // Try to get it from sessionStorage first (for unsaved work in progress)
    const tempProgression = sessionStorage.getItem('tempUserProgression');
    if (tempProgression) {
      // Un sessionStorage corrompu (ecriture interrompue, quota, bidouille
      // manuelle) ne doit pas faire tomber la lecture d'une progression : on
      // retombe sur la version sauvegardee.
      try {
        return JSON.parse(tempProgression);
      } catch (err) {
        console.warn('[progressions] progression temporaire illisible:', err.message);
      }
    }
    // Otherwise check if it's saved
    const userProgressions = getUserProgressions();
    if (userProgressions['User Progression']) {
      return userProgressions['User Progression'];
    }
    // Return null if not found
    return null;
  }
  
  // First check built-in progressions
  if (CHORD_PROGRESSIONS[name]) {
    return CHORD_PROGRESSIONS[name];
  }
  
  // Then check user progressions
  const userProgressions = getUserProgressions();
  return userProgressions[name] || null;
};

// Convert scale degree (1-based) to index in a scale array (0-based)
// Handles wrapping around for degrees > scale length
const degreeToIndex = (degree, scaleLength) => {
  // Adjust to 0-based index
  const index = (degree - 1) % scaleLength;
  // Handle negative indices (for flat degrees)
  return index < 0 ? index + scaleLength : index;
};

// Get the chord notes for a progression in a specific scale
const getProgressionChords = (progressionName, notesInMode) => {
  const progression = getProgressionByName(progressionName);
  if (!progression || !notesInMode || notesInMode.length === 0) {
    return [];
  }
  
  const scaleLength = notesInMode.length;
  return progression.degrees.map(degree => {
    const index = degreeToIndex(degree, scaleLength);
    return notesInMode[index];
  });
};

export {
  CHORD_PROGRESSIONS,
  MODAL_PROGRESSIONS,
  getUserProgressions,
  saveUserProgression,
  getProgressionNames,
  getProgressionByName,
  getProgressionChords,
  degreeToIndex
};
